<template>
  <aside ref="sidebarRef" :class="isCollapsed ? 'w-20' : 'w-64'"
    class="fixed top-0 left-0 h-screen p-4 flex flex-col bg-gray-900/80 backdrop-blur-xl text-gray-200 shadow-lg transition-all duration-300 ease-in-out z-30 border-r border-gray-700/60">
    <div class="flex items-center" :class="isCollapsed ? 'justify-center' : 'justify-between'">
      <span v-if="!isCollapsed" class="text-2xl font-bold text-white whitespace-nowrap">GSPE</span>
      <button @click="toggleSidebar" class="p-1.5 rounded-lg hover:bg-gray-700/60 focus:outline-none">
        <ChevronDoubleLeftIcon class="h-6 w-6 text-white transition-transform duration-300"
          :class="{ 'rotate-180': isCollapsed }" />
      </button>
    </div>

    <hr class="my-4 border-t border-gray-700/60" />

    <nav class="flex-grow overflow-y-auto">
      <div v-for="item in menuItems" :key="item.title">
        <div v-if="item.children" class="my-1">
          <div @click="toggleSubMenu(item)"
            class="flex items-center justify-between p-3 rounded-lg hover:bg-gray-700/40 transition-colors duration-200 cursor-pointer">
            <div class="flex items-center">
              <DuotoneIcon :icon="item.icon" :color="item.color" :class="{ 'mx-auto': isCollapsed }" />
              <span v-if="!isCollapsed" class="ml-4 whitespace-nowrap">{{ item.title }}</span>
            </div>
            <ChevronRightIcon v-if="!isCollapsed" class="h-4 w-4 transition-transform"
              :class="{ 'rotate-90': item.isOpen }" />
          </div>
          <div class="overflow-hidden transition-all duration-300 ease-in-out"
            :style="{ maxHeight: item.isOpen && !isCollapsed ? (item.children.length * 50) + 'px' : '0px' }">
            <router-link v-for="child in item.children" :key="child.title" :to="child.route"
              class="flex items-center p-3 pl-12 my-1 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-gray-700/40"
              active-class="bg-blue-600/50 text-white">
              <component :is="child.icon" class="h-5 w-5 mr-3" />
              <span class="whitespace-nowrap">{{ child.title }}</span>
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <div class="mt-auto">
      <hr class="my-4 border-t border-gray-700/60" />
      <a href="#" @click.prevent="logout"
        class="flex items-center p-3 rounded-lg hover:bg-red-600/70 transition-colors duration-200">
        <DuotoneIcon :icon="ArrowLeftOnRectangleIcon" color="text-red-400" :class="{ 'mx-auto': isCollapsed }" />
        <span v-if="!isCollapsed" class="ml-4 whitespace-nowrap">Logout</span>
      </a>
    </div>
  </aside>
</template>

<script>
import {
  ChevronDoubleLeftIcon, ArrowLeftOnRectangleIcon, BuildingOffice2Icon,
  DocumentPlusIcon, PencilIcon, ClipboardDocumentListIcon, ShieldCheckIcon, PrinterIcon,
  CurrencyDollarIcon, ChartBarIcon, ArchiveBoxIcon, InboxArrowDownIcon, ChevronRightIcon,
} from '@heroicons/vue/24/outline';
// Impor komponen DuotoneIcon yang baru dibuat
import DuotoneIcon from '../Layout/DuotoneIcon.vue';

export default {
  name: 'SidebarPage',
  components: {
    // Daftarkan komponen DuotoneIcon
    DuotoneIcon,
    // Daftarkan semua ikon yang digunakan
    ChevronDoubleLeftIcon, ArrowLeftOnRectangleIcon, BuildingOffice2Icon, DocumentPlusIcon,
    PencilIcon, ClipboardDocumentListIcon, ShieldCheckIcon, PrinterIcon, CurrencyDollarIcon,
    ChartBarIcon, ArchiveBoxIcon, InboxArrowDownIcon, ChevronRightIcon,
  },
  data() {
    return {
      isCollapsed: false,
      menuItems: [
        {
          title: 'Misc. Company', icon: BuildingOffice2Icon, isOpen: false,
          color: 'text-indigo-400',
          children: [
            { title: 'Entry Subcon', icon: DocumentPlusIcon, route: '/dashboard/subcon/entry' },
            { title: 'Update Subcon', icon: PencilIcon, route: '/dashboard/subcon/update' },
          ]
        },
        {
          title: 'SPK Subcon', icon: ClipboardDocumentListIcon, isOpen: false,
          color: 'text-sky-400',
          children: [
            { title: 'Entry SPK', icon: DocumentPlusIcon, route: '/dashboard/spksubcon/entry' },
            { title: 'Authorization', icon: ShieldCheckIcon, route: '/dashboard/spksubcon/auth' },
            { title: 'Update SPK', icon: PencilIcon, route: '/dashboard/spksubcon/update' },
            { title: 'Print SPK', icon: PrinterIcon, route: '/dashboard/spksubcon/print' },
          ]
        },
        {
          title: 'Invoice', icon: CurrencyDollarIcon, isOpen: false,
          color: 'text-emerald-400',
          children: [
            { title: 'Entry Invoice', icon: DocumentPlusIcon, route: '/dashboard/invoice/entry' },
            { title: 'Update Invoice', icon: PencilIcon, route: '/dashboard/invoice/update' },
            { title: 'Report Invoice', icon: ChartBarIcon, route: '/dashboard/invoice/report' },
            { title: 'Print Invoice', icon: PrinterIcon, route: '/dashboard/invoice/print' },
          ]
        },
        {
          title: 'Document GR Template', icon: ArchiveBoxIcon, isOpen: false,
          color: 'text-amber-400',
          children: [
            { title: 'Entry', icon: DocumentPlusIcon, route: '/dashboard/document-gr/template-entry' },
            { title: 'Update', icon: PencilIcon, route: '/dashboard/document-gr/template-update' },
            { title: 'Report', icon: ChartBarIcon, route: '/dashboard/document-gr/template-report' },
          ]
        },
        {
          title: 'Goods Receipt', icon: InboxArrowDownIcon, isOpen: false,
          color: 'text-rose-400',
          children: [
            { title: 'Entry', icon: DocumentPlusIcon, route: '/dashboard/goods-receipt/entry' },
            { title: 'Update', icon: PencilIcon, route: '/dashboard/goods-receipt/update' },
            { title: 'Report', icon: ChartBarIcon, route: '/dashboard/goods-receipt/report' },
          ]
        }
      ]
    };
  },
  mounted() {
    // Menambahkan event listener ke seluruh dokumen saat komponen dimuat
    document.addEventListener('mousedown', this.handleClickOutside);
  },
  beforeUnmount() {
    // PENTING: Hapus event listener saat komponen akan dihancurkan untuk mencegah memory leak
    document.removeEventListener('mousedown', this.handleClickOutside);
  },
  methods: {
    // Method untuk menangani klik di luar sidebar
    handleClickOutside(event) {
      // Cek jika sidebar sedang terbuka DAN elemen yang diklik BUKAN bagian dari sidebar
      if (!this.isCollapsed && this.$refs.sidebarRef && !this.$refs.sidebarRef.contains(event.target)) {
        // Jika ya, ciutkan sidebar
        this.isCollapsed = true;
        this.$emit('sidebar-toggled', this.isCollapsed); // Kirim status ke parent
      }
    },

    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit('sidebar-toggled', this.isCollapsed);
    },
    toggleSubMenu(clickedItem) {
      if (this.isCollapsed) {
        this.toggleSidebar();
        setTimeout(() => {
          clickedItem.isOpen = true;
        }, 300);
        return;
      }
      // Logika accordion: tutup menu lain yang terbuka saat membuka menu baru
      this.menuItems.forEach(item => {
        if (item !== clickedItem && item.children) {
          item.isOpen = false;
        }
      });
      clickedItem.isOpen = !clickedItem.isOpen;
    },
    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },
  },
};
</script>

<style scoped>
/* Menambahkan style untuk scrollbar agar lebih estetik di browser Chrome/Safari */
nav::-webkit-scrollbar {
  width: 6px;
}

nav::-webkit-scrollbar-thumb {
  background-color: #4a5568;
  /* gray-600 */
  border-radius: 3px;
}

nav::-webkit-scrollbar-track {
  background-color: transparent;
}
</style>